import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { ShoppingBag, Search, User, Menu, Heart, LogOut, Store, Shield, X, ChevronDown, BadgeCheck, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import NotificationsBell from "@/components/NotificationsBell";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

import souqMadarMark from "@/assets/brand/souq-madar-mark.png";

export default function Header() {
    const { t, lang, setLang } = useLang();
    const { user, logout } = useAuth();
    const { cart } = useCart();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [q, setQ] = useState("");
    const [chatUnread, setChatUnread] = useState(0);

    useEffect(() => {
        if (!user) return setChatUnread(0);
        const load = () => api.get("/chat/unread-count").then((r) => setChatUnread(r.data.unread)).catch(() => {});
        load();
        const iv = setInterval(load, 15000);
        return () => clearInterval(iv);
    }, [user]);

    const onSearch = (e) => {
        e.preventDefault();
        navigate(`/products?q=${encodeURIComponent(q)}`);
        setMobileOpen(false);
    };

    return (
        <header className="sticky top-0 z-40 glass border-b border-border/70" data-testid="site-header">
            <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center gap-4">
                <Link to="/" className="flex items-center gap-2 no-tap-highlight" data-testid="logo-link">
                    <div className="w-10 h-10 rounded-2xl overflow-hidden shrink-0">
                        <img
                            src={souqMadarMark}
                            alt={t("appName")}
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <div className="hidden sm:block">
                        <div className="font-black text-lg text-espresso leading-none">{t("appName")}</div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">{t("tagline")}</div>
                    </div>
                </Link>

                <form
                    onSubmit={onSearch}
                    className="hidden md:flex flex-1 max-w-xl mx-4 items-center bg-surface rounded-full ps-4 pe-2 py-1.5 border border-border"
                    data-testid="search-form"
                >
                    <Search className="w-4 h-4 text-muted-foreground me-2" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder={t("search")}
                        className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground"
                        data-testid="search-input"
                    />
                    <Button
                        type="submit"
                        size="sm"
                        className="rounded-full bg-terracotta hover:bg-terracotta/90 text-white h-8"
                        data-testid="search-submit-btn"
                    >
                        {t("searchGo")}
                    </Button>
                </form>

                <nav className="hidden lg:flex items-center gap-1 text-sm font-medium">
                    <Link to="/products" className="px-3 py-2 rounded-full hover:bg-surface transition-colors" data-testid="nav-products">
                        {t("products")}
                    </Link>
                    <Link to="/stores" className="px-3 py-2 rounded-full hover:bg-surface transition-colors" data-testid="nav-stores">
                        {t("stores")}
                    </Link>
                    <Link to="/reels" className="px-3 py-2 rounded-full hover:bg-surface transition-colors" data-testid="nav-reels">
                        {t("reels")}
                    </Link>
                </nav>

                <div className="ms-auto flex items-center gap-2">
                    <button
                        onClick={() => setLang(lang === "ar" ? "en" : "ar")}
                        className="text-xs font-bold h-9 w-9 rounded-full bg-surface hover:bg-border transition-colors"
                        data-testid="lang-toggle-btn"
                        aria-label="Toggle language"
                    >
                        {lang === "ar" ? "EN" : "ع"}
                    </button>

                    <Link to="/favorites" className="hidden sm:inline-flex" data-testid="nav-favorites">
                        <Button variant="ghost" size="icon" className="rounded-full h-10 w-10">
                            <Heart className="w-5 h-5" />
                        </Button>
                    </Link>

                    <NotificationsBell />

                    {user && (
                        <Link to="/chat" className="relative hidden sm:inline-flex" data-testid="nav-chat">
                            <Button variant="ghost" size="icon" className="rounded-full h-10 w-10">
                                <MessageSquare className="w-5 h-5" />
                            </Button>
                            {chatUnread > 0 && (
                                <span className="absolute top-1 end-1 bg-terracotta text-white text-[9px] font-bold rounded-full h-4 min-w-[16px] px-1 grid place-items-center" data-testid="chat-unread-badge">
                                    {chatUnread}
                                </span>
                            )}
                        </Link>
                    )}

                    <Link to="/cart" className="relative" data-testid="nav-cart">
                        <Button variant="ghost" size="icon" className="rounded-full h-10 w-10">
                            <ShoppingBag className="w-5 h-5" />
                        </Button>
                        {cart.items?.length > 0 && (
                            <span
                                className="absolute -top-1 -end-1 bg-terracotta text-white text-[10px] font-bold rounded-full h-5 min-w-[20px] px-1 grid place-items-center"
                                data-testid="cart-count"
                            >
                                {cart.items.reduce((s, i) => s + i.quantity, 0)}
                            </span>
                        )}
                    </Link>

                    {user ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="rounded-full gap-1.5" data-testid="user-menu-btn">
                                    <User className="w-4 h-4" />
                                    <span className="hidden sm:inline max-w-[80px] truncate">{user.name}</span>
                                    <ChevronDown className="w-3 h-3" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="min-w-[200px]">
                                <DropdownMenuItem asChild>
                                    <Link to="/orders" data-testid="menu-orders">{t("myOrders")}</Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild>
                                    <Link to="/favorites" data-testid="menu-favorites">{t("favorites")}</Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild>
                                    <Link to="/chat" data-testid="menu-chat">
                                        <MessageSquare className="w-4 h-4 me-2" />
                                        {t("chats")}
                                        {chatUnread > 0 && <span className="ms-auto text-[10px] bg-terracotta text-white rounded-full px-1.5 py-0.5">{chatUnread}</span>}
                                    </Link>
                                </DropdownMenuItem>
                                {user.role === "seller" && (
                                    <DropdownMenuItem asChild>
                                        <Link to="/seller" className="gap-2 flex items-center" data-testid="menu-seller">
                                            <Store className="w-4 h-4" />
                                            {t("sellerDashboard")}
                                        </Link>
                                    </DropdownMenuItem>
                                )}
                                {user.role === "admin" && (
                                    <DropdownMenuItem asChild>
                                        <Link to="/admin" className="gap-2 flex items-center" data-testid="menu-admin">
                                            <Shield className="w-4 h-4" />
                                            {t("adminDashboard")}
                                        </Link>
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="gap-2">
                                    <LogOut className="w-4 h-4" />
                                    {t("logout")}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : (
                        <div className="hidden sm:flex items-center gap-1.5">
                            <Link to="/login" data-testid="nav-login">
                                <Button variant="ghost" size="sm" className="rounded-full">
                                    {t("login")}
                                </Button>
                            </Link>
                            <Link to="/register" data-testid="nav-register">
                                <Button size="sm" className="rounded-full bg-terracotta hover:bg-terracotta/90 text-white">
                                    {t("register")}
                                </Button>
                            </Link>
                        </div>
                    )}

                    <button
                        className="lg:hidden ms-1"
                        onClick={() => setMobileOpen(!mobileOpen)}
                        data-testid="mobile-menu-btn"
                    >
                        {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>

            {mobileOpen && (
                <div className="lg:hidden border-t border-border bg-white" data-testid="mobile-menu">
                    <div className="p-4 space-y-3">
                        <form onSubmit={onSearch} className="flex items-center bg-surface rounded-full px-4 py-2">
                            <Search className="w-4 h-4 text-muted-foreground me-2" />
                            <input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder={t("search")}
                                className="flex-1 bg-transparent outline-none text-sm"
                                data-testid="mobile-search-input"
                            />
                        </form>
                        <Link to="/products" onClick={() => setMobileOpen(false)} className="block py-2 px-3 rounded-lg hover:bg-surface">
                            {t("products")}
                        </Link>
                        <Link to="/stores" onClick={() => setMobileOpen(false)} className="block py-2 px-3 rounded-lg hover:bg-surface">
                            {t("stores")}
                        </Link>
                        {!user && (
                            <div className="grid grid-cols-2 gap-2 pt-2">
                                <Link to="/login" onClick={() => setMobileOpen(false)}>
                                    <Button variant="outline" className="w-full rounded-full">{t("login")}</Button>
                                </Link>
                                <Link to="/register" onClick={() => setMobileOpen(false)}>
                                    <Button className="w-full rounded-full bg-terracotta text-white">{t("register")}</Button>
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
